import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, GitBranch, DollarSign, BarChart3, Target, Users, Zap } from "lucide-react";
import { formatCurrency, getGoalIcon, statusColor, progressColor } from "./goalFormat";

const valueLabel = (goal, field) => (goal.unit === "dollars" ? formatCurrency(goal[field] || 0) : `${goal[field] || 0} ${goal.unit}`);

const Decomposition = ({ d }) => (
  <div className="mt-3 pt-3 border-t border-gray-700/50 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="goal-decomposition">
    {[
      ["Pipeline Needed", formatCurrency(d.pipelineNeeded)],
      ["Leads Needed", d.leadsNeeded],
      ["Activities", d.activitiesNeeded],
      ["Close Rate", `${(d.conversionRate * 100).toFixed(0)}%`],
    ].map(([label, value]) => (
      <div key={label} className="text-center">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-white">{value}</p>
      </div>
    ))}
  </div>
);

const SubGoalCard = ({ goal, depth = 0, expanded, onToggle }) => {
  const Icon = getGoalIcon(goal.type, goal.name);
  const isExpanded = !!expanded[goal.goalId];
  const hasChildren = goal.children?.length > 0;
  const progress = goal.progress || 0;
  const isRevenue = goal.type === "revenue";

  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className={depth > 0 ? "ml-6 border-l-2 border-gray-700 pl-4" : ""} data-testid={`goal-card-${goal.goalId}`}>
      <div className={`p-4 rounded-lg border mb-3 ${isRevenue ? "bg-ism-teal/10 border-ism-teal/30" : "bg-white/5 border-gray-700/50"}`}>
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {hasChildren && (
              <button onClick={() => onToggle(goal.goalId)} className="p-1 hover:bg-white/10 rounded" data-testid={`goal-toggle-${goal.goalId}`}>
                {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
              </button>
            )}
            <div className={`p-2 rounded-lg ${isRevenue ? "bg-ism-teal/20" : "bg-white/10"}`}>
              <Icon className={`h-5 w-5 ${isRevenue ? "text-ism-teal" : "text-gray-400"}`} />
            </div>
            <div className="min-w-0">
              <h4 className="font-normal text-white">{goal.name}</h4>
              <p className="text-xs text-gray-500">{goal.description}</p>
            </div>
          </div>
          <Badge className={`${statusColor(goal.status)} border-0 text-xs flex-shrink-0`}>{goal.status?.replace("_", " ").toUpperCase()}</Badge>
        </div>

        <div className="mb-2">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">{valueLabel(goal, "currentValue")}</span>
            <span className="text-white">{valueLabel(goal, "targetValue")}</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, progress)}%` }} transition={{ duration: 0.5, ease: "easeOut" }} className={`h-full ${progressColor(progress)} rounded-full`} />
          </div>
          <div className="text-right mt-1"><span className="text-xs text-gray-500">{progress.toFixed(0)}% complete</span></div>
        </div>

        {goal.decomposition && <Decomposition d={goal.decomposition} />}
      </div>

      <AnimatePresence>
        {isExpanded && hasChildren && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
            {goal.children.map((child) => <SubGoalCard key={child.goalId} goal={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} />)}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const METRICS = [
  { key: "currentRevenue", label: "Revenue", icon: DollarSign, color: "text-ism-teal", money: true },
  { key: "currentPipeline", label: "Pipeline", icon: BarChart3, color: "text-amber-400", money: true },
  { key: "opportunitiesCreated", label: "Opportunities", icon: Target, color: "text-gray-300" },
  { key: "leadsGenerated", label: "Leads", icon: Users, color: "text-gray-300" },
  { key: "activitiesLogged", label: "Activities", icon: Zap, color: "text-orange-400" },
];

const MetricsGrid = ({ metrics }) => (
  <div className="mt-6 pt-6 border-t border-gray-700">
    <h4 className="text-sm text-gray-400 mb-4">Current Performance Metrics</h4>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
      {METRICS.map(({ key, label, icon: Icon, color, money }) => (
        <div key={key} className="text-center p-3 bg-white/5 rounded-lg" data-testid={`metric-${key}`}>
          <Icon className={`h-5 w-5 ${color} mx-auto mb-1`} />
          <p className="text-lg text-white">{money ? formatCurrency(metrics[key]) : metrics[key]}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      ))}
    </div>
  </div>
);

export const GoalHierarchyCard = ({ hierarchy }) => {
  const root = { ...hierarchy.rootGoal, children: hierarchy.subGoals || [] };
  const [expanded, setExpanded] = useState({ [root.goalId]: true });
  const toggle = (goalId) => setExpanded((prev) => ({ ...prev, [goalId]: !prev[goalId] }));

  return (
    <Card className="glass-card border-gray-800/50 p-6" data-testid="goal-hierarchy-card">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h3 className="text-xl font-normal text-white flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-ism-teal" /> Goal Hierarchy
        </h3>
        <p className="text-sm text-gray-500">Auto-decomposed from your revenue target</p>
      </div>
      <SubGoalCard goal={root} expanded={expanded} onToggle={toggle} />
      {hierarchy.metrics && <MetricsGrid metrics={hierarchy.metrics} />}
    </Card>
  );
};
